import { Capacitor } from '@capacitor/core'

const PROD_WEB_ORIGIN = 'https://www.resactly.com'

function isProdEnvironment(): boolean {
  // Prefer an explicit env var if you have one
  const env = process.env.NODE_ENV
  const appEnv = process.env.REACT_APP_MODE // if you use it (prod/dev/qa/local)

  if (appEnv) return appEnv === 'production'
  return env === 'production'
}

/**
 * The canonical web origin for building SHAREABLE links (referrals, group-session invites, etc.).
 *
 * On native shells `window.location.origin` is `capacitor://localhost` (or `ionic://localhost`),
 * which only resolves inside the app on that device — useless as a link a recipient can open. This
 * returns a real https origin instead: the prod web domain in prod builds, `REACT_APP_WEB_BASE_URL`
 * for non-prod native builds, and the plain browser origin on the web.
 */
export function getWebOrigin(): string {
  const origin = window.location.origin

  // Native shells report capacitor://localhost (or ionic://localhost)
  const isNative =
    typeof Capacitor !== 'undefined' &&
    (Capacitor.isNativePlatform?.() ?? Capacitor.getPlatform() !== 'web')

  if (isNative) {
    // Only force prod domain when we're in prod builds
    if (isProdEnvironment()) return PROD_WEB_ORIGIN

    // For non-prod native builds, prefer an explicit base URL if you have it
    // e.g. REACT_APP_WEB_BASE_URL=https://dev.resactly.com
    const configured = process.env.REACT_APP_WEB_BASE_URL
    if (configured) return configured.replace(/\/$/, '')

    // Fallback: keep origin (may be capacitor://localhost) but prevents undefined
    return origin
  }

  // Web stays canonical
  return origin
}

/**
 * Builds the canonical referral URL format: https://<app-domain>/r/<referralCode>
 */
export function buildReferralUrl(referralCode: string): string {
  return `${getWebOrigin()}/r/${referralCode}`
}